// src/apis/apiServices/DocumentType.js
import axios from "../axiosInstance";

const prefix = "/DocumentType";

/**
 * Helper: build candidate request bodies we will try.
 * Order: direct payload, { Request: payload }, { request: payload }.
 */
function buildCandidates(payload) {
  return [
    { body: payload, label: "direct" },
    { body: { Request: payload }, label: "RequestWrapper_Pascal" },
    { body: { request: payload }, label: "RequestWrapper_lower" },
  ];
}

async function tryPostVariants(url, payload, config = {}) {
  const candidates = buildCandidates(payload);
  let lastErr = null;

  for (const cand of candidates) {
    try {
      console.debug("[documentTypeService] trying POST", { url, variant: cand.label, body: cand.body });
      const resp = await axios.post(url, cand.body, { headers: { "Content-Type": "application/json" }, ...config });
      console.debug("[documentTypeService] POST success", { url, variant: cand.label, status: resp?.status, data: resp?.data });
      return { success: true, resp, variant: cand.label };
    } catch (err) {
      console.warn("[documentTypeService] POST variant failed", { url, variant: cand.label, status: err?.response?.status, data: err?.response?.data });
      lastErr = err;
      // if 400/422 it's validation; continue to try other variants to find one accepted
      // for some network errors we might break, but we will keep trying.
    }
  }

  // all failed
  throw lastErr;
}

async function tryPutVariants(urls, payload, config = {}) {
  // urls: array of candidate urls to try in order
  const candidates = buildCandidates(payload);
  let lastErr = null;

  for (const u of urls) {
    for (const cand of candidates) {
      try {
        console.debug("[documentTypeService] trying PUT", { url: u, variant: cand.label, body: cand.body });
        const resp = await axios.put(u, cand.body, { headers: { "Content-Type": "application/json" }, ...config });
        console.debug("[documentTypeService] PUT success", { url: u, variant: cand.label, status: resp?.status, data: resp?.data });
        return { success: true, resp, url: u, variant: cand.label };
      } catch (err) {
        console.warn("[documentTypeService] PUT variant failed", { url: u, variant: cand.label, status: err?.response?.status, data: err?.response?.data });
        lastErr = err;
      }
    }
  }

  throw lastErr;
}

const getDocumentTypeList = async () => {
  try {
    const response = await axios.get(`${prefix}/getDocumentTypeList`);
    return response?.data;
  } catch (error) {
    console.error("[documentTypeService] getDocumentTypeList error:", {
      message: error?.message,
      status: error?.response?.status,
      data: error?.response?.data,
    });
    throw error;
  }
};

const getDocumentTypeById = async (id) => {
  try {
    const response = await axios.get(`${prefix}/getDocumentTypeById/${id}`);
    return response?.data;
  } catch (error) {
    console.error("[documentTypeService] getDocumentTypeById error:", {
      message: error?.message,
      status: error?.response?.status,
      data: error?.response?.data,
    });
    throw error;
  }
};

const addDocumentType = async (payload) => {
  try {
    const serverPayload = mapToServerShape(payload);
    console.debug("[documentTypeService] addDocumentType serverPayload:", serverPayload);

    // try variants until one succeeds
    const result = await tryPostVariants(`${prefix}/createDocumentType`, serverPayload);
    // return response body (preserve original shape)
    return result.resp?.data;
  } catch (error) {
    console.error("[documentTypeService] addDocumentType error:", {
      message: error?.message,
      status: error?.response?.status,
      data: error?.response?.data,
    });
    throw error;
  }
};

const updateDocumentType = async (payload) => {
  try {
    const serverPayload = mapToServerShape(payload);
    const id = serverPayload.Id ?? 0;
    if (!id || id === 0) throw new Error("Missing Id for updateDocumentType");

    console.debug("[documentTypeService] updateDocumentType serverPayload:", serverPayload);

    // Try common URL patterns in order:
    // 1) /updateDocumentType/{id}
    // 2) /updateDocumentType
    const urls = [`${prefix}/updateDocumentType/${id}`, `${prefix}/updateDocumentType`];

    const result = await tryPutVariants(urls, serverPayload);
    return result.resp?.data;
  } catch (error) {
    console.error("[documentTypeService] updateDocumentType error:", {
      message: error?.message,
      status: error?.response?.status,
      data: error?.response?.data,
    });
    throw error;
  }
};

const deleteDocumentType = async (id) => {
  try {
    const response = await axios.delete(`${prefix}/deleteDocumentType/${id}`);

    if (response?.status === 204) return { isSuccess: true, message: "Deleted", status: 204 };
    if (typeof response?.data !== "undefined") return response.data;
    return { isSuccess: true, message: "Deleted", status: response?.status };
  } catch (error) {
    console.error("[documentTypeService] deleteDocumentType error:", {
      message: error?.message,
      status: error?.response?.status,
      data: error?.response?.data,
      url: `${prefix}/deleteDocumentType/${id}`,
    });
    throw error;
  }
};

// map incoming JS model (camelCase) to server expected PascalCase keys
function mapToServerShape(p = {}) {
  const cand =
    p.ReminderAlertDefaultDays ??
    p.reminderAlertDefaultDays ??
    p.reminer_alert_default_days ??
    p.reminder_alert_default_days ??
    null;

  let rad = null;
  if (cand === null || typeof cand === "undefined" || cand === "") {
    // default to 0 so strict int fields on server don't fail
    rad = 0;
  } else if (typeof cand === "number" && Number.isFinite(cand)) {
    rad = Math.trunc(cand);
  } else {
    const parsed = Number.parseInt(String(cand).trim(), 10);
    rad = Number.isFinite(parsed) ? parsed : 0;
  }

  const payload = {
    Id: p.id ?? p.Id ?? 0,
    DocumentName: (p.documentName ?? p.DocumentName ?? "").toString(),
    Description: (p.description ?? p.Description ?? "").toString(),
    IsIssueDateEnable: !!(p.isIssueDateEnable ?? p.IsIssueDateEnable),
    IsExpiryDateEnable: !!(p.isExpiryDateEnable ?? p.IsExpiryDateEnable),
    IsReminderAlertEnable: !!(p.isReminderAlertEnable ?? p.IsReminderAlertEnable),
    ReminderAlertDefaultDays: rad,
    IsRenewable: !!(p.isRenewable ?? p.IsRenewable),
    HaveDocumentNo: !!(p.haveDocumentNo ?? p.HaveDocumentNo),
    DocumentCategoryId: p.documentCategoryId ?? p.DocumentCategoryId ?? null,
    CompanyId: p.companyId ?? p.CompanyId ?? 0,
    ModifiedBy: p.ModifiedBy ?? p.modifiedBy ?? null,
  };

  // Remove undefined keys only
  Object.keys(payload).forEach((k) => {
    if (typeof payload[k] === "undefined") delete payload[k];
  });

  return payload;
}

export const documentTypeService = {
  getDocumentTypeList,
  getDocumentTypeById,
  addDocumentType,
  updateDocumentType,
  deleteDocumentType,
};

export default documentTypeService;
